// Orentis wordmark logo — pixel-faithful SVG replica of the brand identity mockup.
// Layout:
//   ┌ Silver ring (O) ┐
//   │   Gold C inside  │  ← C is smaller than ring; opens to the right; visible gap
//   └─────────────────┘
//   O R [E-bars] N T I S    ← Archivo Black wordmark, gold "E" made of 3 bars
//   ── CONSULTING ──         ← gold subtitle flanked with short gold lines
//   STRATEGY , SCALE , SUCCESS   ← silver, comma-separated tagline
import { useEffect, useState } from "react";

function useResponsiveSize(sizes) {
  const pick = () => {
    if (typeof window === "undefined") return sizes.md || sizes.lg;
    const w = window.innerWidth;
    if (w < 640) return sizes.sm || sizes.md || sizes.lg;
    if (w < 1024) return sizes.md || sizes.lg;
    return sizes.lg || sizes.xl || sizes.md;
  };
  const [v, setV] = useState(pick);
  useEffect(() => {
    const on = () => setV(pick());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line
  }, []);
  return v;
}

export default function OrentisLogo({
  size = "md",
  responsive,
  showTag = true,
  showTagline = false,
  layout = "horizontal",
  className = "",
}) {
  const sizes = {
    sm: { wm: 14, ct: 8, tl: 7, gap: 10 },
    md: { wm: 20, ct: 9, tl: 8, gap: 12 },
    lg: { wm: 34, ct: 13, tl: 10, gap: 18 },
    xl: { wm: 56, ct: 20, tl: 13, gap: 26 },
    xxl: { wm: 96, ct: 30, tl: 17, gap: 40 },
  };
  const responsiveSize = useResponsiveSize(responsive || {});
  const effectiveSize = responsive ? responsiveSize : size;
  const s = sizes[effectiveSize] || sizes.md;
  // Ring diameter matched to the wordmark cap-height for visual balance.
  // The mark JPG has a dark padding baked around the ring, so we scale it up
  // to keep the visible ring proportional to the wordmark.
  const markPx = Math.round(s.wm * 1.9);

  const wmFont = {
    fontFamily:
      "'Archivo Black', 'Inter', 'Space Grotesk', ui-sans-serif, sans-serif",
    fontWeight: 900,
    letterSpacing: "0.02em",
  };

  const Wordmark = ({ px }) => {
    // Archivo Black cap-height ≈ 0.72 of font-size
    const capH = px * 0.72;
    const barH = capH * 0.13;
    const gap = (capH - 3 * barH) / 2;
    // E cap width ≈ 0.6 of font-size
    const eWidth = px * 0.6;
    const midBarWidth = eWidth * 0.8;

    return (
      <span
        className="inline-flex items-baseline leading-none whitespace-nowrap"
        style={{
          ...wmFont,
          color: "#dcdcdc",
          fontSize: px,
          lineHeight: 1,
        }}
      >
        <span>OR</span>
        {/* E — 3 gold bars occupying the full cap-height, aligned to baseline */}
        <span
          className="inline-block"
          aria-label="E"
          style={{
            width: eWidth,
            height: capH,
            marginLeft: px * 0.04,
            marginRight: px * 0.04,
            verticalAlign: "baseline",
            display: "inline-block",
          }}
        >
          <span
            style={{
              display: "block",
              width: "100%",
              height: barH,
              background: "#c9a24b",
            }}
          />
          <span style={{ display: "block", height: gap }} />
          <span
            style={{
              display: "block",
              width: `${(midBarWidth / eWidth) * 100}%`,
              height: barH,
              background: "#c9a24b",
            }}
          />
          <span style={{ display: "block", height: gap }} />
          <span
            style={{
              display: "block",
              width: "100%",
              height: barH,
              background: "#c9a24b",
            }}
          />
        </span>
        <span>NTIS</span>
      </span>
    );
  };

  const Mark = ({ px }) => {
    // Use the actual brand mark image — sharp, crisp, matches the brand identity exactly.
    // Slight brightness/contrast boost + higher-quality rendering to counter JPG haze.
    return (
      <img
        src="/logos/orentis-mark.jpg"
        alt="Orentis"
        width={px * 2}
        height={px * 2}
        className="shrink-0 select-none"
        style={{
          display: "block",
          width: px,
          height: px,
          objectFit: "contain",
          imageRendering: "auto",
          filter: "brightness(1.12) contrast(1.15) saturate(1.1)",
        }}
        draggable={false}
      />
    );
  };

  const Consulting = ({ px }) => (
    <div
      className="flex items-center justify-center"
      style={{ gap: px * 0.4 }}
    >
      <span
        className="bg-[#c9a24b]"
        style={{ height: 1, width: px * 1.2, opacity: 0.9 }}
      />
      <span
        style={{
          color: "#c9a24b",
          letterSpacing: "0.32em",
          fontSize: px,
          ...wmFont,
          fontWeight: 700,
          paddingLeft: "0.2em",
        }}
      >
        CONSULTING
      </span>
      <span
        className="bg-[#c9a24b]"
        style={{ height: 1, width: px * 1.2, opacity: 0.9 }}
      />
    </div>
  );

  const Tagline = ({ px }) => (
    <div
      style={{
        color: "#dcdcdc",
        letterSpacing: "0.38em",
        fontSize: px,
        ...wmFont,
        fontWeight: 400,
      }}
    >
      STRATEGY , SCALE , SUCCESS
    </div>
  );

  if (layout === "stack") {
    return (
      <div className={`inline-flex flex-col items-center ${className}`}>
        <Mark px={Math.round(markPx * 1.7)} />
        <div
          style={{ marginTop: s.gap * 0.55 }}
          className="flex flex-col items-center"
        >
          <Wordmark px={s.wm} />
          {showTag && (
            <div className="mt-3">
              <Consulting px={s.ct} />
            </div>
          )}
          {showTagline && (
            <div className="mt-3">
              <Tagline px={s.tl} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // horizontal
  return (
    <div className={`inline-flex items-center ${className}`} style={{ gap: s.gap }}>
      <Mark px={markPx} />
      <div className="flex flex-col items-start leading-none">
        <Wordmark px={s.wm} />
        {showTag && (
          <div className="mt-1.5 self-stretch">
            <Consulting px={s.ct} />
          </div>
        )}
        {showTagline && (
          <div className="mt-2 self-stretch flex justify-center">
            <Tagline px={s.tl} />
          </div>
        )}
      </div>
    </div>
  );
}
